/**
 * GR 결과 화면의 판정(총괄 DEV-I-007, 디자인 결과 화면 v0.1 + 총괄 결정). DOM 없이 단위 테스트한다.
 *
 * - 화면은 정오를 계산하지 않는다. 서버 GET /attempts/:id/grading의 points[].state·result만 쓴다.
 * - pending은 '결과 대기'. 맞음·틀림 집계에 넣지 않는다. 새 응답이 대기이면 그 지점의 이전 정오를 남기지 않는다.
 * - 문항과 결과는 지점 ID로 연결한다(표시 번호·배열 순서만으로 연결하지 않음).
 * - 함께 보낸 요청(결과·문항·과제) 중 하나라도 401·403·404면 다른 요청이 성공해도 결과를 표시하지 않는다.
 * - 문항 조회만 통신 오류·5xx면 결과의 지점별 목록만 표시한다.
 */
import type { ApiResult } from './transport.js';
import type { AssignmentInfo, ItemsResponse } from './types.js';

export type ResultMark = 'correct' | 'incorrect' | 'pending';
export interface GradingPoint { readonly pointId: string; readonly displayNumber: string; readonly state: 'graded' | 'pending'; readonly result?: 'correct' | 'incorrect' }
export interface GradingResponse { readonly attemptId: string; readonly submitted: boolean; readonly gradingComplete: boolean; readonly points: readonly GradingPoint[] }

export const markOf = (p: GradingPoint): ResultMark => (p.state === 'graded' && (p.result === 'correct' || p.result === 'incorrect') ? p.result : 'pending');

export type BatchDecision =
  | { readonly kind: 'relogin' }
  | { readonly kind: 'denied' }            // 403 content_not_released
  | { readonly kind: 'account' }           // 그 밖의 403 → 계정 상태 확인
  | { readonly kind: 'notfound' }          // 404
  | { readonly kind: 'error'; readonly cause: 'network' | 'server' | 'attempt_mismatch' | 'malformed' | 'timeout' }
  | { readonly kind: 'notsubmitted' }
  | {
      readonly kind: 'ok';
      readonly grading: GradingResponse;
      /** 문항: 새로 받음 / 이번에 요청하지 않음(이전 값 유지) / 통신 오류·5xx·시도 불일치 등으로 실패(목록만) */
      readonly items: { readonly kind: 'fresh'; readonly data: ItemsResponse } | { readonly kind: 'keep' } | { readonly kind: 'failed'; readonly cause: string };
      readonly assignment: AssignmentInfo | null;
    };

/** 묶음이 다 끝나기 전에 도착한 401·403·404 하나로 정하는 판정(DEV-I-008). 나머지 요청은 기다리지 않는다 */
export function decideEarly(r: Extract<ApiResult, { kind: 'http' }>): Extract<BatchDecision, { kind: 'relogin' | 'denied' | 'account' | 'notfound' }> {
  if (r.status === 401) return { kind: 'relogin' };
  if (r.status === 403) return r.body['error'] === 'content_not_released' ? { kind: 'denied' } : { kind: 'account' };
  return { kind: 'notfound' };
}

const httpStatus = (r: ApiResult | null): number | null => (r !== null && r.kind === 'http' ? r.status : null);

/** 결과·문항·과제 묶음 응답 판정. attemptId: 요청한 시도. items·assignment는 이번에 요청하지 않았으면 null */
export function decideBatch(attemptId: string, grading: ApiResult, items: ApiResult | null, assignment: ApiResult | null): BatchDecision {
  const all = [grading, items, assignment];
  if (all.some((r) => httpStatus(r) === 401)) return { kind: 'relogin' };
  const forbidden = all.filter((r) => httpStatus(r) === 403) as Extract<ApiResult, { kind: 'http' }>[];
  if (forbidden.length > 0) return forbidden.some((r) => r.body['error'] === 'content_not_released') ? { kind: 'denied' } : { kind: 'account' };
  if (all.some((r) => httpStatus(r) === 404)) return { kind: 'notfound' };
  if (grading.kind === 'network') return { kind: 'error', cause: 'network' };
  if (grading.status !== 200) return { kind: 'error', cause: 'server' };
  const g = grading.body as Partial<GradingResponse>;
  if (g.attemptId !== attemptId) return { kind: 'error', cause: 'attempt_mismatch' };
  if (typeof g.submitted !== 'boolean' || !Array.isArray(g.points)) return { kind: 'error', cause: 'malformed' };
  if (!g.submitted) return { kind: 'notsubmitted' };
  let it: Extract<BatchDecision, { kind: 'ok' }>['items'];
  if (items === null) it = { kind: 'keep' };
  else if (items.kind === 'network') it = { kind: 'failed', cause: 'network' };
  else if (items.status !== 200) it = { kind: 'failed', cause: `http_${items.status}` };
  else if (items.body['attemptId'] !== attemptId) it = { kind: 'failed', cause: 'attempt_mismatch' };
  else it = { kind: 'fresh', data: items.body as unknown as ItemsResponse };
  const a = assignment !== null && assignment.kind === 'http' && assignment.status === 200 ? (assignment.body as unknown as AssignmentInfo) : null;
  return { kind: 'ok', grading: g as GradingResponse, items: it, assignment: a };
}

export interface ResultSummary {
  readonly total: number; readonly correct: number; readonly incorrect: number; readonly pending: number;
  /** none: GR 지점 없음 · pending: 전체 대기 · partial: 일부 결과 · complete: 전체 결과 */
  readonly kind: 'none' | 'pending' | 'partial' | 'complete';
}

export function summarize(g: GradingResponse): ResultSummary {
  const n = { correct: 0, incorrect: 0, pending: 0 };
  for (const p of g.points) n[markOf(p)]++;
  const graded = n.correct + n.incorrect;
  const kind = g.points.length === 0 ? 'none' : graded === 0 ? 'pending' : graded === g.points.length && g.gradingComplete ? 'complete' : 'partial';
  return { total: g.points.length, ...n, kind };
}

/** 총괄 결정 문구 */
export const RESULT_HEADLINE: Record<ResultSummary['kind'], string> = {
  pending: '아직 확인할 수 있는 GR 결과가 없어요.',
  partial: '일부 GR 지점의 결과만 확인할 수 있어요.',
  complete: '모든 GR 지점의 결과를 확인할 수 있어요.',
  none: '이 과제에는 결과를 확인할 GR 지점이 없어요.',
};
export const MARK_LABEL: Record<ResultMark, string> = { correct: '✓ 맞음', incorrect: '✕ 틀림', pending: '결과 대기' };
export const MARK_WORD: Record<ResultMark, string> = { correct: '맞음', incorrect: '틀림', pending: '결과 대기' };

/** 같은 시도에서 직전에 받은 결과와 비교: 정오가 있던 지점이 대기가 되거나 정오가 달라진 지점(대기 → 정오는 바뀜이 아님) */
export function changedPoints(prev: GradingResponse | null, next: GradingResponse): Set<string> {
  const out = new Set<string>();
  if (prev === null) return out;
  for (const p of prev.points) {
    if (markOf(p) === 'pending') continue;
    const n = next.points.find((x) => x.pointId === p.pointId);
    if (n === undefined || markOf(n) !== markOf(p)) out.add(p.pointId);
  }
  return out;
}

export interface PointJoin {
  /** 결과의 모든 지점이 문항 응답의 같은 ID·같은 표시 번호 지점과 맞는가(아니면 문장에 넣지 않고 목록만) */
  readonly matched: boolean;
  /** 지점 ID → 학생이 고른 보기 문구(문항 응답 grSaved의 보기 ID로 찾음). 없으면 null */
  readonly selectedLabel: ReadonlyMap<string, string | null>;
}

/** 저장된 선택 보기 ID 집합. 복수 선택은 selectedOptionIds(scalar는 NULL), 기존 단일 이력은 scalar로 호환 */
export function savedOptionIds(s: { readonly selectedOptionId: string | null; readonly selectedOptionIds?: readonly string[] } | undefined): readonly string[] {
  if (s === undefined) return [];
  if (s.selectedOptionIds !== undefined && s.selectedOptionIds.length > 0) return s.selectedOptionIds;
  return s.selectedOptionId !== null ? [s.selectedOptionId] : [];
}

/** 선택한 보기 문구들(보기 목록 순서)을 ', '로 잇는다. 선택이 없거나 보기를 못 찾으면 null */
export function labelsOfSelection(options: readonly { id: string; label: string }[], ids: readonly string[]): string | null {
  const set = new Set(ids);
  const labels = options.filter((o) => set.has(o.id)).map((o) => o.label);
  return labels.length > 0 ? labels.join(', ') : null;
}

export function joinPoints(items: ItemsResponse, g: GradingResponse): PointJoin {
  const byId = new Map<string, { displayNumber: string; options: readonly { id: string; label: string }[] }>();
  for (const it of items.items) for (const p of it.grPoints ?? []) byId.set(p.id, p);
  const saved = new Map((items.grSaved ?? []).map((s) => [s.pointId, savedOptionIds(s)]));
  const matched = g.points.every((p) => byId.get(p.pointId)?.displayNumber === p.displayNumber);
  const selectedLabel = new Map<string, string | null>();
  for (const p of g.points) {
    const q = byId.get(p.pointId);
    selectedLabel.set(p.pointId, q === undefined ? null : labelsOfSelection(q.options, saved.get(p.pointId) ?? []));
  }
  return { matched, selectedLabel };
}
